import { useState } from 'react';
import { useListingPagesStore } from '@pages/User/store/listingPages.store';
import { useEffectOnce } from '@src/hooks';
import type {
  ColumnFiltersState,
  ColumnOrderState,
  ColumnPinningState,
  PaginationState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import type { VirtualItem } from '@tanstack/react-virtual';

export interface TableState {
  columnFilters: ColumnFiltersState;
  columnOrder: ColumnOrderState;
  columnPinning: ColumnPinningState;
  columnVisibility: VisibilityState;
  globalFilter: string;
  isGroupByParent: boolean;
  pagination: PaginationState;
  sorting: SortingState;
  measurementsCache?: VirtualItem[];
  topIndex?: number | null;
}

export function mergeInitialWithStored(initialState: TableState, stored: Partial<TableState>): TableState {
  return {
    ...initialState,
    ...stored,
    pagination: {
      ...initialState.pagination,
      ...stored.pagination,
    },
  };
}

export interface UseTableStateOptions {
  entityName: string;
  initialState: TableState;
}

export interface UseTableStateReturn {
  state: TableState | undefined;
  updateState: (updates: Partial<TableState>) => void;
  resetState: () => void;
  isInitialized: boolean;
}

export function useTableState({ entityName, initialState }: UseTableStateOptions): UseTableStateReturn {
  const [isInitializing, setIsInitializing] = useState(true);

  const { listingPageState, setListingPageState } = useListingPagesStore(
    ({ listingPagesState, setListingPagesState }) => ({
      listingPageState: listingPagesState[entityName],
      setListingPageState: setListingPagesState(entityName),
    }),
  );

  useEffectOnce(() => {
    if (listingPageState === undefined) {
      setListingPageState(initialState);
    }
    setIsInitializing(false);
  });

  const updateState = (updates: Partial<TableState>) => {
    setListingPageState(updates);
  };

  const resetState = () => {
    setListingPageState(initialState);
  };

  const state = mergeInitialWithStored(initialState, listingPageState ?? {});

  return {
    state,
    updateState,
    resetState,
    isInitialized: !isInitializing,
  };
}
